import { apiClient } from './api';
import { storage, STORAGE_KEYS } from '@/utils/storage';
import type { Order, OrderStatus, AuthTokens } from '@/types';
import { mapBackendOrderToFrontendOrder } from './orderService';

/**
 * RestaurantOrderService - Restaurant order management API calls
 *
 * Per Restaurant Management Panel: Order listing and status updates
 * Follows pattern from orderService.ts
 */

export interface OrderFilters {
  status?: OrderStatus | 'NEW';
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface OrderStats {
  totalOrders: number;
  totalRevenue: number;
  averageOrderValue: number;
  byStatus: Record<OrderStatus, number>;
}

export interface AssignableCourier {
  id: string;
  status: string;
  courierType: string;
  vehicleType: string | null;
  plateNumber: string | null;
  activeOrdersCount: number;
  maxCapacity: number;
  name: string | null;
  phone: string | null;
}

export interface AssignableCouriersResponse {
  couriers: AssignableCourier[];
  deliveryProviderType: 'PLATFORM' | 'OWN_FLEET' | 'HYBRID';
}

export const restaurantOrderService = {
  /**
   * Get restaurant orders with filters
   */
  async getRestaurantOrders(filters: OrderFilters = {}): Promise<{
    orders: Order[];
    total: number;
    page: number;
    limit: number;
  }> {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const queryString = params.toString();
    const url = `/restaurant/my/orders${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<{
      orders: any[];
      total: number;
      page: number;
      limit: number;
    }>(url);

    return {
      orders: response.data.data.orders.map(mapBackendOrderToFrontendOrder),
      total: response.data.data.total,
      page: response.data.data.page,
      limit: response.data.data.limit,
    };
  },

  /**
   * Get order details
   */
  async getOrder(orderId: string): Promise<Order> {
    const response = await apiClient.get<any>(`/restaurant/my/orders/${orderId}`);
    return mapBackendOrderToFrontendOrder(response.data.data);
  },

  async getAssignableCouriers(orderId: string): Promise<AssignableCouriersResponse> {
    const response = await apiClient.get<AssignableCouriersResponse>(
      `/restaurant/my/orders/${orderId}/assignable-couriers`,
    );
    return {
      couriers: response.data.data.couriers ?? [],
      deliveryProviderType: response.data.data.deliveryProviderType ?? 'PLATFORM',
    };
  },

  async assignCourier(orderId: string, courierId: string): Promise<Order> {
    const response = await apiClient.patch<any>(`/restaurant/my/orders/${orderId}/courier`, {
      courierId,
    });
    return mapBackendOrderToFrontendOrder(response.data.data);
  },

  /**
   * Update order status
   */
  async updateOrderStatus(orderId: string, status: OrderStatus): Promise<Order> {
    const response = await apiClient.patch<any>(`/restaurant/my/orders/${orderId}/status`, {
      status,
    });
    return mapBackendOrderToFrontendOrder(response.data.data);
  },

  /**
   * Get order statistics
   */
  async getOrderStats(period: 'today' | 'week' | 'month' = 'today'): Promise<OrderStats> {
    const response = await apiClient.get<OrderStats>(
      `/restaurant/my/orders/stats?period=${period}`,
    );
    return response.data.data;
  },

  /**
   * Reject order
   */
  async rejectOrder(
    orderId: string,
    reason: 'TOO_BUSY' | 'OUT_OF_STOCK' | 'RESTAURANT_CLOSED' | 'OUT_OF_SERVICE_AREA' | 'BELOW_MINIMUM' | 'OTHER',
    message?: string,
  ): Promise<Order> {
    const payload: { reason: string; message?: string } = { reason };
    if (reason === 'OTHER' && message && message.trim()) {
      payload.message = message.trim();
    }
    const response = await apiClient.patch<any>(`/restaurant/my/orders/${orderId}/reject`, payload);
    return mapBackendOrderToFrontendOrder(response.data.data);
  },

  /**
   * Print receipt
   */
  async printReceipt(orderId: string, type: 'receipt' | 'kitchen' = 'receipt'): Promise<void> {
    const baseURL = import.meta.env.VITE_API_URL || '/api';
    const url = `${baseURL}/restaurant/my/orders/${orderId}/receipt?type=${type}`;
    
    const tokens = storage.get<AuthTokens>(STORAGE_KEYS.AUTH_TOKENS);
    const token = tokens?.access_token;
    
    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch receipt');
    }

    const html = await response.text();
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      throw new Error('Popup blocked. Please allow popups for this site.');
    }

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
    }, 250);
  },
};
